import { useLocalization } from '@components/Localization'
import { useNotifications } from '@components/NotificationsProvider'
import QuickCart from '@components/_product-detail/QuickCart'
import { Button } from '@gassan-ui'
import { useAddArticleToCartMutation, useRemoveItemFromCartMutation } from '@generated'
import { imgix } from '@lib/imigx'
import { useRouter } from 'next/router'
import React, { FC, useState } from 'react'
import * as ReactGA from 'react-ga'
import SelectSize from './SelectSize'
import { useCart } from '@lib/hooks/useCart'
import { useCartReferenceStore } from '@components/LoadCart'

type Props = {
  articleId: string
  variant: 'regular' | 'ring'
}

const AddToCart: FC<Props> = ({ articleId, variant }) => {
  const [showQuickCart, setShowQuickCart] = useState(false)
  const [size, setSize] = useState<string | null>(null)
  const { messages, lang } = useLocalization()
  const notifications = useNotifications()
  const router = useRouter()
  const cart = useCart()
  const cartReferenceStore = useCartReferenceStore()
  const reference = cartReferenceStore.reference
  const [sizeHasError, setSizeHasError] = useState(false)
  const [showMobileSizePicker, setShowMobileSizePicker] = useState(false)
  const [, removeItemFromCart] = useRemoveItemFromCartMutation()
  const [result, addArticleToCart] = useAddArticleToCartMutation()

  const handleRemoveItemFromCart = async cartItemId => {
    if (!cart) {
      return
    }
    const item = cart.items.find(item => item.id === cartItemId)
    try {
      await removeItemFromCart({ reference, cartItemId })
      if (cart.items.length === 1) {
        setShowQuickCart(false)
      }
      notifications.addNotification({
        variant: 'image-action-undo',
        label: item.article.brand,
        sublabel: item.article.description,
        image: imgix(item.article.images[0], { width: 180 }),
        description: lang === 'nl' ? 'Verwijderd uit winkelmand' : 'Removed from shopping cart',
        actionLabel: lang === 'nl' ? 'Herstel' : 'Undo',
        onClickAction: () => handleAddArticleToCart(item.article.id, item.quantity)
      })
      ReactGA.event({
        category: 'Ecommerce',
        action: 'removeFromCart'
      })
    } catch (error) {
      console.error(error)
      notifications.addNotification({
        variant: 'error',
        label: messages.notifications.refreshAndTryAgain
      })
    }
  }

  const handleAddArticleToCart = async (articleId: string, quantity: number) => {
    try {
      await addArticleToCart({
        reference,
        articleId,
        quantity,
        size: variant === 'ring' ? size : null
      }).then(data => {
        const article = data.data?.addArticleToCart?.items.find(
          item => item.article.id === articleId
        )
        setSize(null)
        const prefix = lang === 'nl' ? '/nl' : '/en'
        ReactGA.event({
          category: 'Ecommerce',
          action: 'addToCart'
        })
        if (window.innerWidth <= 640) {
          notifications.addNotification({
            variant: 'image-action',
            label: article.article.brand,
            sublabel: article.article.description,
            image: imgix(article.article.images[0], { width: 180 }),
            description: lang === 'nl' ? 'Toegevoegd aan winkelmand' : 'Added to shopping cart',
            actionLabel: lang === 'nl' ? 'Bekijk' : 'View',
            onClickAction: () =>
              router.push(
                `/[lang]/[...slug]`,
                `${prefix}${messages.navigation.secondaryNav.cart.url}`
              )
          })
        } else {
          setShowQuickCart(true)
        }
      })
    } catch (error) {
      notifications.addNotification({
        variant: 'error',
        label: messages.notifications.refreshAndTryAgain
      })
    }
  }

  const handleOrderClick = () => {
    if (variant === 'ring') {
      if (window.innerWidth < 1024) {
        setShowMobileSizePicker(true)
      } else {
        size === null ? setSizeHasError(true) : handleAddArticleToCart(articleId, 1)
      }
    } else {
      handleAddArticleToCart(articleId, 1)
    }
  }

  const handleConfirmSize = () => {
    setShowMobileSizePicker(false)
    handleAddArticleToCart(articleId, 1)
  }

  const handleSetSize = (size: string) => {
    setSize(size)
    setSizeHasError(false)
  }

  return (
    <>
      {variant === 'ring' && (
        <SelectSize
          onRequestClose={() => setShowMobileSizePicker(false)}
          onSelectSize={size => handleSetSize(size)}
          onConfirm={() => handleConfirmSize()}
          selectedSize={size}
          showMobileSizePicker={showMobileSizePicker}
          sizeHasError={sizeHasError}
        />
      )}

      <Button
        status={result.fetching ? 'loading' : 'idle'}
        variant="dark"
        onClick={() => handleOrderClick()}
      >
        {messages.productDetail.orderOnline}
      </Button>

      {showQuickCart && (
        <QuickCart
          onRequestClose={() => setShowQuickCart(false)}
          handleRemoveItemFromCart={id => handleRemoveItemFromCart(id)}
        />
      )}
    </>
  )
}

export default AddToCart
